import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Flex, Text } from '@chakra-ui/react'
import { Asset, OrderAssetReason, OrderAssetState, RequestOrigin } from '@leid/api-assets'
import { PickupStatus } from '@leid/api-orders'

import { LeidYellowButton } from 'components/ui/Buttons/Button'
import { Loader } from 'components/ui/Loader'
import apiClient from 'helpers/clients/apiClient'
import { displaySnackbar } from 'store/partials/snackbar/snackbarSlice'
import { utcNow } from 'utils/dates'

import { ReturnAssetsStepData, SelectedAssetData } from '../models'

type Props = {
  data: ReturnAssetsStepData
  onStepSuccess: () => void
  stopFlow: () => void
  setAssetChangeRequest: (request: SelectedAssetData) => void
}

const ReturnAssetStep = ({ data, onStepSuccess, stopFlow, setAssetChangeRequest }: Props): JSX.Element => {
  const { t } = useTranslation('common')
  const dispatch = useDispatch()

  const [isUpdating, setIsUpdating] = useState(false)

  const returnAsset = async () => {
    setIsUpdating(true)

    const orderItemResponse = await apiClient.getOrderItem(data.orderItemId)
    if (!orderItemResponse.success || !orderItemResponse.data) {
      dispatch(displaySnackbar({ message: t('error'), isError: true }))
      stopFlow()
      return
    }

    const orderItem = orderItemResponse.data

    if (
      orderItem.orderItem.pickupStatus === PickupStatus.INSPECTED ||
      orderItem.orderItem.pickupStatus === PickupStatus.COMPLETED
    ) {
      stopFlow()
      setIsUpdating(false)
      return
    }

    let asset: Asset | null | undefined
    if (orderItem.orderItem.assetId) {
      const assetResponse = await apiClient.getAsset(orderItem.orderItem.assetId)
      if (!assetResponse.success || !assetResponse.data) {
        dispatch(displaySnackbar({ message: t('error'), isError: true }))
        stopFlow()
        return
      }

      asset = assetResponse.data
    }

    const assetChangeRequest: SelectedAssetData = {
      reason: OrderAssetReason.SELF_REPORTED,
      state: OrderAssetState.RETURNED,
      timestamp: utcNow(),
      orderId: orderItem.orderItem.orderId,
      orderItemId: data.orderItemId,
      assetId: asset?.id,
      locationId: orderItem.orderItem.locationId,
      sublocationId: asset?.default_sub_location_id,
      origin: RequestOrigin.UNKNOWN,
      visualTag: asset?.visual_tag ?? '',
    }
    setAssetChangeRequest(assetChangeRequest)

    onStepSuccess()

    setIsUpdating(false)
  }

  return (
    <>
      <Text fontWeight={300}>{t('returnConfirmationMessage')}</Text>

      <LeidYellowButton onClick={returnAsset}>
        <Flex flexDirection="row" justifyContent="safe center" alignItems="center" width="100%" gridGap="12px">
          {isUpdating && <Loader size="md" width="auto" margin="0" />}
          {t('returnAsset')}
        </Flex>
      </LeidYellowButton>
    </>
  )
}

export default ReturnAssetStep
